import { useEffect, useState } from "react";
import { BlockEditor } from "@fk/editor";
import '@fk/editor/dist/editor.css';
import { convertOutlineToTiptapJson, convertTiptapToOutlineJson } from "../../utils/tiptapConverter.ts";
import useOutlineStore from "../../stores/modules/outline.ts";
import { findMatchingNodesFromSection } from "../../utils/outlineNodeExtractor.ts";
import { flushSync } from "react-dom";
import { Editor } from "@tiptap/core";
import eventBus, { EventType } from "../../hooks/useEventBus.ts";
import useAiTypeStore from "../../stores/modules/aiType.ts";
import { AIDataType } from "../../types/Writer/AIData.ts";
import { PaperService } from "../../services/paperService.ts";
import { useParams } from "react-router-dom";
import { LatexReviewModal } from "../PaperEditor/LatexReviewModal.tsx";
import FigureReviewModal from "../PaperEditor/FigureReviewModal.tsx";
import { useToast } from "@chakra-ui/react";

export const BlockEditorApp = ({ editRef, setEditor, tiptapJson, handleSave, zoomLevel }: any) => {
  const [content, setContent] = useState({
    type: "doc",
    content: []
  })
  const outlineStore = useOutlineStore()
  const [isHere, setIsHere] = useState(false)
  const [curIndex, setCurIndex] = useState(0)
  const { updateAiType } = useAiTypeStore()
  const [canEditable, setCanEditable] = useState(true)
  const { paperId } = useParams();
  const [showCompile, setShowCompile] = useState(false)
  const [sectionNode, setSectionNode] = useState<any>({})
  const [showFigure,setShowFigure] = useState(false)
  const [figureInfo, setFigureInfo] = useState<any>({})
  const toast = useToast();

  const zoom = parseFloat(zoomLevel || "100");
  // 100% -> 1rem, 110% -> 1.125rem (assuming 1rem = 16px, 2px increase is 0.125rem)
  const fontSize = 1 + ((zoom - 100) / 10) * 0.125;

  useEffect(() => {
    if (tiptapJson && !isHere && tiptapJson.content?.length) {
      // if (tiptapJson && tiptapJson.content?.length) {
      queueMicrotask(() => {
        flushSync(() => {
          // 你的同步更新代码
          if (tiptapJson?.content?.length) {
            tiptapJson.content?.forEach((item) => {
              if (item.type === 'codeBlock' && item.content?.length === 1 && item.content[0].text === '') {
                item.content = []
              }
            })
          }
          setContent(tiptapJson)
        });
      });
    } else {
      setIsHere(false)
    }
  }, [tiptapJson]);

  useEffect(() => {
    try {
      sessionStorage.setItem('do_outline', JSON.stringify(outlineStore.outline))
    } catch (error) {
      console.log(error)
    }
  }, [outlineStore.outline]);

  useEffect(() => {
    setCanEditable(outlineStore.isEditable)
  }, [outlineStore.isEditable]);

  useEffect(() => {
    setCurIndex(outlineStore.currentSectionIndex)
  }, [outlineStore.currentSectionIndex])

  const handleEditorCreated = (editor: Editor) => {
    setEditor(editor);

    // Fix: browser selects word AFTER contextmenu event on right-click.
    // Track right-click, then undo the selection in the next selectionchange.
    if (editor?.view?.dom) {
      const dom = editor.view.dom;
      let rightClickPos: number | null = null;

      dom.addEventListener('contextmenu', () => {
        // Save cursor pos; browser will select a word after this event
        rightClickPos = editor.state.selection.from;
      });

      document.addEventListener('selectionchange', () => {
        if (rightClickPos === null) return;
        if (!dom.contains(document.activeElement)) return;

        const sel = window.getSelection();
        if (sel && !sel.isCollapsed) {
          // Browser just auto-selected a word — collapse it back
          rightClickPos = null;
          sel.collapseToStart();
        }
      });
    }
  }

  function rephraseHandler(AIDataType: AIDataType.rephrase | AIDataType.refine, EventType: EventType.Rephrase | EventType.Refine) {
    updateAiType(AIDataType)
    setTimeout(() => {
      Promise.resolve().then(() => {
        const list = JSON.parse(
          localStorage.getItem("select_content") || "[]"
        );

        // Step 1: Convert Tiptap selection to Outline format (with potentially wrong IDs)
        const tiptapResult = convertTiptapToOutlineJson({
          content: [
            {
              type: "heading",
              attrs: {
                textAlign: null,
                level: 1,
                color: "#1D8AFF",
                id: 1,
              },
              content: [
                {
                  type: "text",
                  text: "New Section",
                },
              ],
            },
            ...list,
          ],
        }).children;

        // Add select_content to each node
        tiptapResult?.forEach((item) => {
          if (['ul', 'ol'].includes(item.type)) {
            const processChildren = (node: any) => {
              if (node?.children?.length > 0) {
                if (!['ul', 'ol'].includes(node.type)) {
                  node.select_content = node.content.replace(
                    /<\/(em|strong|u)>$/,
                    ""
                  );
                }
                node.children.forEach((child: any) => processChildren(child));
              } else {
                node.select_content = node.content;
              }
            }
            processChildren(item);
          } else {
            item.select_content = item.content?.replace(
              /<\/(em|strong|u)>$/,
              ""
            ) || '';
          }
        });

        // Step 2: Get the current sectionNode from outline store
        const { outline, currentSectionIndex } = useOutlineStore.getState();
        const sectionNode = outline?.nodes?.[currentSectionIndex];

        // Step 3: Find matching nodes with correct IDs from sectionNode
        let result = tiptapResult;
        if (sectionNode && tiptapResult?.length) {
          const matchedNodes = findMatchingNodesFromSection(sectionNode, tiptapResult);
          if (matchedNodes.length > 0) {
            result = matchedNodes;
          }
        }

        eventBus.emit(EventType, result)
      })
    }, 1000)

  }

  const saveTiptap = (content: any) => {
    console.log(content, 11)
    const newSection = convertTiptapToOutlineJson(content, outlineStore.currentSectionIndex)
    try { // todo 临时方案
      // todo 这里最好是convertTiptapToOutlineJson时content修改，不必再调用一次convertOutlineToTiptapJson
      const newContent = convertOutlineToTiptapJson(newSection)
      // todo newContent里只有label，没有id?
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { state, view } = editRef.current?.editor; // 子组件的 editor 实例
      const { doc, tr } = state;
      doc.forEach((node, offset, index) => {
        if (node.type.name !== 'paragraph') {
          tr.setNodeMarkup(offset, undefined, {
            ...node.attrs,
            label: newContent.content[index].attrs?.label,
            id: newContent.content[index].attrs?.id,
          });
        } else {
          tr.setNodeMarkup(offset, undefined, {
            ...node.attrs,
            id: newContent.content[index].attrs?.id,
          });
        }
      });

      if (tr.docChanged) {
        view.dispatch(
          tr.setMeta('addToHistory', false) // 不入栈
        );
      }
    } catch (error) {
      console.error(error)
    }
    handleSave(newSection)
    setIsHere(true)
  }

  return (
    <div>
      <style>{`
        .ProseMirror * {
          font-size: ${fontSize}rem !important;
        }
        .ProseMirror h1 { font-size: ${fontSize * 1.75}rem !important; }
        .ProseMirror h2 { font-size: ${fontSize * 1.5}rem !important; }
        .ProseMirror h3 { font-size: ${fontSize * 1.25}rem !important; }
        .ProseMirror h4 { font-size: ${fontSize}rem !important; }
        .ProseMirror h5 { font-size: ${fontSize * 0.83}rem !important; }
        .ProseMirror h6 { font-size: ${fontSize * 0.67}rem !important; }
        .ProseMirror span[data-type="inline-math"] { padding-right: 0 !important; overflow: hidden !important; }
        .ProseMirror span[data-type="inline-math"]::-webkit-scrollbar { display: none !important; }
      `}</style>
      {/*<Header editRef={editRef} editor={editor} rephraseHandler={rephraseHandler}/>*/}
      <div className="container pb-32">
        {
          canEditable ? <BlockEditor
          key={curIndex}
          ref={editRef}
          content={content}
          onCreate={handleEditorCreated}
          onUpdate={setEditor}
          toastCallback={toast}
          compileLatexHandler={(node) => {
            const content =convertTiptapToOutlineJson({ "type": "doc",
              "content": [
                  node
                ]
              })
            setShowCompile(true)
            setSectionNode(content?.children?.[0])
          }}
          onSave={(content: any) => {
            saveTiptap(content)
          }}
          onBlur={(_: any, json: any) => {
            saveTiptap(json)
          }}
          rephraseHandler={() => rephraseHandler(AIDataType.rephrase, EventType.Rephrase)}
          refineHandler={() => rephraseHandler(AIDataType.refine, EventType.Refine)}
          uploadCb={async (file:any) =>{
            const res = await PaperService.uploadImage(paperId, file)
            return Promise.resolve({
              fileName: res.file_name,
              fileUrl: res.file_url
            })
          }}
          expandFigureHandler={(content) => {
            setFigureInfo(content)
            setShowFigure(true)
          }}
        /> :
          <BlockEditor
            key={curIndex}
            content={content}
            readOnly={true}
            isDisabled={true}
            editable={false}
            onCreate={handleEditorCreated}
            rephraseHandler={rephraseHandler}
            toastCallback={toast}
            // uploadCb={async (file:any) =>{
            //   const res = await PaperService.uploadImage(paperId, file)
            //   return Promise.resolve({
            //     fileName: res.file_name,
            //     fileUrl: res.file_url
            //   })
            // }}
          />
        }
        
      </div>

      { showCompile && <LatexReviewModal isPaperViewerOpen={showCompile} onPaperViewerClose={setShowCompile} paperId={paperId} sectionNode={sectionNode} /> }
      { showFigure && <FigureReviewModal figureInfo={figureInfo} setShowFigure={setShowFigure} />}
    </div>
  );
}
